import type { CustomField } from '@/common/api/types/customField';
import type {
  ItemCheckboxValue,
  ItemCurrencyValue,
  ItemCustomFieldValue,
  ItemDateValue,
  ItemItemValue,
  ItemLinkValue,
  ItemMultiCustomerValue,
  ItemMultiItemValue,
  ItemMultiLineTextValue,
  ItemMultiLinkValue,
  ItemMultiPicklistValue,
  ItemMultiTextValue,
  ItemMultiUserValue,
  ItemNoteValue,
  ItemNumericValue,
  ItemPicklistValue,
  ItemTextValue,
  ItemUserValue,
} from '@/common/api/types/items';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { CustomFieldValue } from './CustomFieldValue';
import { toApiFieldValue } from './toApiFieldValue';

export interface CustomFieldValueEditorProps {
  customField: CustomField;
  itemTypesById: ItemTypeMap;
  value?: ItemCustomFieldValue;
  isEditable?: boolean;
  showLabel?: boolean;
  autoFocus?: boolean;
  onUpdate: (value: unknown) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
}

export const CustomFieldValueEditor = ({
  customField,
  itemTypesById,
  value,
  isEditable = false,
  showLabel = true,
  autoFocus = false,
  onUpdate,
  isPending,
  isSuccess,
  isError,
  error,
}: CustomFieldValueEditorProps) => {
  const { type: fieldType } = customField;

  const getCurrentValue = (): unknown => {
    switch (fieldType) {
      case 'CHECKBOX':
        return (value as ItemCheckboxValue | undefined)?.CHECKBOX;
      case 'CURRENCY':
        return (value as ItemCurrencyValue | undefined)?.CURRENCY;
      case 'DATE':
        return (value as ItemDateValue | undefined)?.DATE;
      case 'ITEM':
        return (value as ItemItemValue | undefined)?.ITEM;
      case 'LINK':
        return value as ItemLinkValue | undefined;
      case 'MULTI_LINK':
        return value as ItemMultiLinkValue[] | undefined;
      case 'MULTI_ITEM':
        return value as ItemMultiItemValue[] | undefined;
      case 'MULTILINE_TEXT':
        return (value as ItemMultiLineTextValue | undefined)?.MULTILINE_TEXT;
      case 'MULTI_PICKLIST':
        return value as ItemMultiPicklistValue[] | undefined;
      case 'MULTI_CUSTOMER':
        return value as ItemMultiCustomerValue[] | undefined;
      case 'MULTI_TEXT':
        return value as ItemMultiTextValue[] | undefined;
      case 'MULTI_USER':
        return value as ItemMultiUserValue[] | undefined;
      case 'NOTE':
        return (value as ItemNoteValue | undefined)?.NOTE;
      case 'NUMERIC':
        return (value as ItemNumericValue | undefined)?.NUMERIC;
      case 'PICKLIST':
        return (value as ItemPicklistValue | undefined)?.PICKLIST;
      case 'TEXT':
        return (value as ItemTextValue | undefined)?.TEXT;
      case 'USER':
        return (value as ItemUserValue | undefined)?.USER;
      default:
        return undefined;
    }
  };

  const isSameValue = (newValue: unknown, currentValue: unknown): boolean => {
    if (fieldType === 'LINK') {
      const a = newValue as ItemLinkValue | undefined;
      const b = currentValue as ItemLinkValue | undefined;
      if (a === undefined && b === undefined) return true;
      if (a === undefined || b === undefined) return false;
      return a.LINK === b.LINK && a.description === b.description;
    }
    if (fieldType === 'MULTI_LINK') {
      const a = (newValue as Array<{ url?: string; description?: string }> | undefined) ?? [];
      const b =
        (currentValue as Array<{ MULTI_LINK?: string; description?: string }> | undefined) ?? [];
      if (a.length !== b.length) return false;
      const keyA = (l: { url?: string; description?: string }) =>
        `${l.url ?? ''}\x00${l.description ?? ''}`;
      const keyB = (l: { MULTI_LINK?: string; description?: string }) =>
        `${l.MULTI_LINK ?? ''}\x00${l.description ?? ''}`;
      return JSON.stringify(a.map(keyA).sort()) === JSON.stringify(b.map(keyB).sort());
    }
    if (Array.isArray(newValue) && Array.isArray(currentValue)) {
      if (newValue.length !== currentValue.length) return false;
      const sorted = (arr: unknown[]) => [...arr].map(String).sort();
      return JSON.stringify(sorted(newValue)) === JSON.stringify(sorted(currentValue));
    }
    return newValue === currentValue;
  };

  const handleChange = (newValue: unknown) => {
    if (isSameValue(newValue, getCurrentValue())) {
      return;
    }

    onUpdate(toApiFieldValue(fieldType, newValue));
  };

  return (
    <CustomFieldValue
      customField={customField}
      itemTypesById={itemTypesById}
      value={value}
      isEditable={isEditable}
      onChange={handleChange}
      isPending={isPending}
      isSuccess={isSuccess}
      isError={isError}
      error={error}
      hideLabel={!showLabel}
      autoFocus={autoFocus}
    />
  );
};
